// Tiny Engineer on a phone's screen (Snap, core/render-dom.js), where there is no 3D room to stand in: the session
// in front shows its robot as a small badge in the live plate at the bottom — its color, its name, what it is doing — and every
// session's robot still says its phrases (voice.js): a bubble beside the badge with the robot's name, and its voice,
// as the switches allow (Robots › Phrases, Sounds). The robots themselves, with desks, helpers and laptops, are in
// the immersive room (robot-crew.js).
//
// One seam: `new RobotBadges({ store, settings, macOf, clientOf, component, palette })`; `update(sessionWindows,
// current)` a few times a second; `drop(spec)` when a window closes.
import { RobotVoice } from './voice.js'
import { engineerMood } from './engineer-mood.js'
import { Extensions } from '../extensions/extensions.js'

const WORDS = { typing: 'working', running: 'running a command', reading: 'reading', thinking: 'thinking', retry: 'retrying', waiting: 'needs you', error: 'hit a problem', done: 'done', asleep: 'asleep', idle: 'idle' }

/// What voice.js asks of a robot, here: its mood, its bubble, and a wave that is only remembered.
class BadgeRobot {
  constructor(badges, spec) { this.badges = badges; this.spec = spec; this.mood = 'idle'; this.hushUntil = 0 }
  bubble(text) { if (text) this.badges.say(this.spec, text) }
  act(mood) { if (mood) this.mood = mood }
}

export class RobotBadges {
  constructor({ store, settings, macOf, clientOf, component = {}, palette = [] }) {
    Object.assign(this, { store, settings, macOf, clientOf, tok: component, palette })
    this.robots = new Map()   // spec → { robot, voice, assigned }
    this.tickedAt = 0
    this.el = document.createElement('div')
    this.el.className = 'robot-badge'
    this.el.hidden = true
    this.el.innerHTML = '<i class="rb-dot"></i><span class="rb-name"></span><span class="rb-mood"></span>'
    this.bubbleEl = document.createElement('div')
    this.bubbleEl.className = 'robot-bubble'
    this.bubbleEl.hidden = true
    // in the live plate at the bottom, where nothing of a window is covered; the phrase just above it
    const live = document.getElementById('live')
    if (live) live.prepend(this.el); else document.body.append(this.el)
    document.body.append(this.bubbleEl)
  }

  colour(i) { return this.palette[((Number(i) || 0) % this.palette.length + this.palette.length) % this.palette.length]?.hex || '#b8583e' }

  moodOf(spec) {
    const ext = Extensions.moodOf(this.macOf(spec)?.key || null, spec.sessionID)
    if (ext) return ext
    const pending = (spec.dom?.permissions?.length || 0) + (spec.dom?.questions?.length || 0)
    return engineerMood({ view: this.clientOf(spec)?.views.get(spec.sessionID), pending, doneSeconds: this.tok.doneSeconds, asleepMinutes: this.tok.asleepMinutes })
  }

  update(sessionWindows, current) {
    const shown = this.settings.get('engineer.shown', true) !== false
    const now = performance.now()
    if (now - this.tickedAt > 1000) { this.tickedAt = now; this.store.tick() }
    const live = new Set(shown ? sessionWindows.filter((s) => s.kind === 'html') : [])
    for (const spec of [...this.robots.keys()]) if (!live.has(spec)) this.drop(spec)
    for (const spec of live) {
      let r = this.robots.get(spec)
      const macKey = this.macOf(spec)?.key || null
      if (!r) {
        const robot = new BadgeRobot(this, spec)
        r = { robot, voice: new RobotVoice(robot, { store: this.store, settings: this.settings, spot: { macKey, sessionID: spec.sessionID } }) }
        this.robots.set(spec, r)
      }
      if (!r.assigned && this.store.canEdit(macKey)) { r.assigned = true; this.store.ensureAssigned(macKey, spec.sessionID) }
      r.robot.mood = this.moodOf(spec)
      r.voice.frame()
    }
    const r = current && this.robots.get(current)
    this.el.hidden = !r
    if (!r) return
    const data = this.store.robotFor(this.macOf(current)?.key || null, current.sessionID)
    this.el.style.setProperty('--robot', this.colour(data?.color))
    this.el.querySelector('.rb-name').textContent = data?.name || 'Tiny Engineer'
    this.el.querySelector('.rb-mood').textContent = WORDS[r.robot.mood] || ''
    this.el.dataset.mood = r.robot.mood
  }

  /// A robot's phrase, in the bubble beside the badge, for about four seconds.
  say(spec, text) {
    const data = this.store.robotFor(this.macOf(spec)?.key || null, spec.sessionID)
    this.bubbleEl.textContent = (data?.name ? data.name + ': ' : '') + text
    this.bubbleEl.style.setProperty('--robot', this.colour(data?.color))
    const live = document.getElementById('live')
    const top = live && !live.hidden ? live.getBoundingClientRect().top : innerHeight
    this.bubbleEl.style.bottom = Math.round(innerHeight - top + 8) + 'px'
    this.bubbleEl.hidden = false
    clearTimeout(this.bubbleTimer)
    this.bubbleTimer = setTimeout(() => { this.bubbleEl.hidden = true }, 4000)
  }

  drop(spec) {
    const r = this.robots.get(spec)
    if (!r) return
    this.robots.delete(spec)
    try { r.voice.farewell() } catch {}
  }

  hide() { this.el.hidden = true; this.bubbleEl.hidden = true }
}
